import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';

// Base container for the table block
const TableBlockContainer = styled.div`
  position: relative;
  margin: 10px 0;
  padding: 0;
  border-radius: 4px;
  background-color: ${props => props.isActive ? 'var(--color-selection-bg, rgba(14, 101, 235, 0.05))' : 'transparent'};
  border: 1px solid ${props => props.isActive ? 'var(--color-selection-border, #dde5f2)' : 'transparent'};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.isActive ? 'var(--color-selection-bg, rgba(14, 101, 235, 0.05))' : 'var(--color-hover-bg, rgba(0, 0, 0, 0.03))'};
    border-color: ${props => props.isActive ? 'var(--color-selection-border, #dde5f2)' : '#e0e0e0'};
  }

  ${props => props.customStyles}
`;

// Table editing container
const TableEditorContainer = styled.div`
  width: 100%;
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  white-space: pre-wrap;
  padding: 8px;
  outline: none;
`;

// Table view container
const TableViewContainer = styled.div`
  width: 100%;
  padding: 8px;
  cursor: pointer;
  
  table {
    border-collapse: collapse;
    width: 100%;
    font-size: 0.9em;
  }
  
  th {
    background-color: #f8f9fa;
    font-weight: 600;
    text-align: left;
    border: 1px solid #dee2e6;
    padding: 8px 12px;
  }
  
  td {
    border: 1px solid #dee2e6;
    padding: 6px 12px;
  }
  
  tr:nth-child(even) {
    background-color: #f8f9fa;
  }
  
  tr:hover {
    background-color: #f1f3f5;
  }
`;

// Helper function to parse markdown table content
const parseTableContent = (markdownContent) => {
  if (!markdownContent) return { rows: [], hasHeader: false };
  
  // Split into lines and clean up
  const lines = Array.isArray(markdownContent) 
    ? markdownContent 
    : markdownContent.split('\n');

  // Parse the rows and cells
  const rows = lines.map(row => {
    // Split the row by pipe, trim cells, and filter empty cells at start/end
    return row.split('|')
      .map(cell => cell.trim())
      .filter((cell, index, array) => 
        index !== 0 && index !== array.length - 1 || cell !== '');
  }).filter(row => row.length > 0); // Filter out empty rows
  
  if (rows.length < 2) return { rows, hasHeader: false };
  
  // Determine if we have a header row (check for separator row)
  const hasHeader = rows.length > 1 && rows[1].every(cell => 
    /^-+$/.test(cell) || 
    /^:-+:?$/.test(cell) || 
    /^:-+$/.test(cell) || 
    /^-+:$/.test(cell)
  );

  return { rows, hasHeader };
};

// Helper function to generate markdown table from data
const generateTableMarkdown = (rows, hasHeader) => {
  if (!rows || rows.length === 0) return '';
  
  // Get the maximum columns needed
  const maxColumns = Math.max(...rows.map(row => row.length));
  
  // Generate the markdown table
  const tableRows = rows.map((row, rowIndex) => {
    // Ensure each row has the same number of columns
    const filledRow = [...row];
    while (filledRow.length < maxColumns) {
      filledRow.push('');
    }
    
    return '| ' + filledRow.join(' | ') + ' |';
  });
  
  // Insert the header separator row if needed
  if (hasHeader && rows.length > 1) {
    const separatorRow = '| ' + Array(maxColumns).fill('---').join(' | ') + ' |';
    tableRows.splice(1, 0, separatorRow);
  }
  
  return tableRows.join('\n');
};

/**
 * TableBlock component for rendering and editing markdown tables
 * 
 * @param {Object} props Component props
 * @param {boolean} props.isActive Whether the table is active/being edited
 * @param {string|Array} props.content Markdown table content
 * @param {Function} props.onClick Handler for click events
 * @param {Function} props.onContentChange Callback when table content changes
 * @param {Function} props.commitContent Callback to commit content changes
 * @param {Object} props.customStyles Custom styles for the container
 */
const TableBlock = React.forwardRef(({
  isActive = false,
  content = '',
  onClick,
  onContentChange,
  commitContent,
  customStyles = {},
  ...otherProps
}, ref) => {
  const editorRef = useRef(null);
  const [initialContent, setInitialContent] = useState(
    Array.isArray(content) ? content.join('\n') : content
  );
  const [isEditing, setIsEditing] = useState(isActive);
  const [parsedTable, setParsedTable] = useState(() => parseTableContent(content));
  const prevIsActiveRef = useRef(isActive);

  // Update content when it changes from parent
  useEffect(() => {
    const contentStr = Array.isArray(content) ? content.join('\n') : content;
    
    // Only update if not actively editing
    if (!isEditing) {
      setInitialContent(contentStr);
      setParsedTable(parseTableContent(content));
    }
  }, [content, isEditing]);

  // Handle active state changes
  useEffect(() => {
    // Handle becoming active
    if (isActive && !prevIsActiveRef.current) {
      setIsEditing(true);
      
      // Focus the editor when it becomes active
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.focus();
          
          // Place cursor at end
          const selection = window.getSelection();
          const range = document.createRange();
          
          if (editorRef.current.lastChild) {
            range.setStartAfter(editorRef.current.lastChild);
          } else {
            range.setStart(editorRef.current, 0);
          }
          
          range.collapse(true);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }, 10);
    }
    
    // Handle becoming inactive
    if (!isActive && prevIsActiveRef.current) {
      // Commit content when going inactive
      if (editorRef.current && commitContent) {
        const currentContent = editorRef.current.innerText;
        if (currentContent !== initialContent) {
          commitContent(currentContent);
          setInitialContent(currentContent);
        }
      }
      setIsEditing(false);
    }
    
    prevIsActiveRef.current = isActive;
  }, [isActive, initialContent, commitContent]);

  // Handle input changes
  const handleInput = useCallback(() => {
    if (editorRef.current && onContentChange) {
      const currentContent = editorRef.current.innerText;
      onContentChange(currentContent);
    }
  }, [onContentChange]);

  // Handle blur events
  const handleBlur = useCallback(() => {
    if (editorRef.current && commitContent) {
      const currentContent = editorRef.current.innerText;
      if (currentContent !== initialContent) {
        commitContent(currentContent);
        setInitialContent(currentContent);
      }
    }
    setIsEditing(false);
  }, [initialContent, commitContent]);

  // Handle key presses
  const handleKeyDown = useCallback((e) => {
    // Handle ESC - revert changes
    if (e.key === 'Escape' && editorRef.current && initialContent !== editorRef.current.innerText) {
      e.preventDefault();
      editorRef.current.innerText = initialContent;
      
      if (onContentChange) {
        onContentChange(initialContent);
      }
      
      setIsEditing(false);
      return;
    }
    
    // Handle Tab in tables - navigate between cells
    if (e.key === 'Tab') {
      e.preventDefault();
      document.execCommand('insertText', false, '  ');
    }
  }, [initialContent, onContentChange]);

  // Render the component
  return (
    <TableBlockContainer
      isActive={isActive}
      customStyles={customStyles}
      ref={ref}
      {...otherProps}
    >
      {isEditing ? (
        <TableEditorContainer
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          dangerouslySetInnerHTML={{ __html: initialContent }}
          onInput={handleInput}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
        />
      ) : (
        <TableViewContainer onClick={onClick}>
          <table>
            <thead>
              {parsedTable.hasHeader && parsedTable.rows.length > 0 && (
                <tr>
                  {parsedTable.rows[0].map((cell, index) => (
                    <th key={`header-${index}`}>{cell}</th>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {parsedTable.rows
                .slice(parsedTable.hasHeader ? 2 : 0)
                .map((row, rowIndex) => (
                  <tr key={`row-${rowIndex}`}>
                    {row.map((cell, cellIndex) => (
                      <td key={`cell-${rowIndex}-${cellIndex}`}>{cell}</td>
                    ))}
                  </tr>
                ))
              }
            </tbody>
          </table>
        </TableViewContainer>
      )}
    </TableBlockContainer>
  );
});

TableBlock.displayName = 'TableBlock';

export default TableBlock;
export { parseTableContent, generateTableMarkdown };
